import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import RateReviewRoundedIcon from '@mui/icons-material/RateReviewRounded';
import { Box, Button, ButtonBase, Chip, Stack, Typography } from '@mui/material';
import { courseIcons } from '../components/courseIcons';
import ProgressRing from '../../student/components/ProgressRing';
import {
    completedLessonCount, coursePercent, getCurrentAssignment, mockCourses, StudentCourse, StudentLesson,
} from '../../student/mockData';
import { getLatestReviewedAttempt } from '../../student/mockFeedback';
import { BLUE, cardSx, GREEN, INTER, NAVY, POPPINS, RING_TRACK, SLATE, SUCCESS_TEXT, yellowButtonSx } from '../../student/studentStyles';
import { StudentDesktopPage } from '../components/StudentDesktopShell';

function CourseCard({ course, onOpen }: { course: StudentCourse; onOpen: () => void }) {
    const CourseIcon = courseIcons[course.icon];
    const completed = completedLessonCount(course);
    return (
        <ButtonBase onClick={onOpen} sx={{ ...cardSx, p: 2.5, minHeight: 152, alignItems: 'stretch', justifyContent: 'flex-start', textAlign: 'left' }}>
            <Stack sx={{ width: '100%' }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{ width: 44, height: 44, borderRadius: '14px', display: 'grid', placeItems: 'center', backgroundColor: `${course.accentColor}1F` }}>
                        <CourseIcon sx={{ color: course.accentColor, fontSize: 24 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 15, color: NAVY, lineHeight: 1.3 }}>{course.name}</Typography>
                        <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE, mt: 0.25 }}>{course.subtitle}</Typography>
                    </Box>
                    <ProgressRing percent={coursePercent(course)} size={42} thickness={5} />
                </Stack>
                <Box sx={{ mt: 'auto', pt: 2 }}>
                    <Box sx={{ height: 6, overflow: 'hidden', borderRadius: 8, backgroundColor: RING_TRACK }}>
                        <Box sx={{ width: `${coursePercent(course)}%`, height: '100%', borderRadius: 8, backgroundColor: course.accentColor }} />
                    </Box>
                    <Typography sx={{ mt: 0.75, fontFamily: INTER, fontSize: 11.5, color: SLATE }}>{coursePercent(course)}% of exercises · {completed} of {course.lessons.length} lessons complete</Typography>
                </Box>
            </Stack>
        </ButtonBase>
    );
}

interface StudentDesktopHomePageProps {
    studentName: string;
    onNavigate: (page: StudentDesktopPage) => void;
    onOpenCourse: (courseId: number) => void;
    onOpenLesson: (courseId: number, lesson: StudentLesson) => void;
}

export default function StudentDesktopHomePage({ studentName, onNavigate, onOpenCourse, onOpenLesson }: StudentDesktopHomePageProps) {
    const current = getCurrentAssignment();
    const latestReviewed = getLatestReviewedAttempt();
    const lessonsDone = mockCourses.reduce((sum, course) => sum + completedLessonCount(course), 0);
    const exercisesDone = mockCourses.reduce((sum, course) => sum + course.lessons.reduce((lessonSum, lesson) => lessonSum + lesson.completedPrompts, 0), 0);

    return (
        <Box sx={{ maxWidth: 1240, mx: 'auto', p: { xs: 2, sm: 3, lg: 4 } }}>
            <Box sx={{ mb: 3 }}>
                <Typography component="h1" sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: { xs: 25, md: 30 }, color: NAVY }}>Hi, {studentName}</Typography>
                <Typography sx={{ mt: 0.4, fontFamily: INTER, fontSize: 14, color: SLATE }}>Ready for a little Chinese today?</Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 2fr) minmax(280px, 1fr)' }, gap: 2.5 }}>
                {current && (
                    <Box sx={{ ...cardSx, p: { xs: 2.5, md: 3.5 }, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' }, gap: 3 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11, letterSpacing: '.09em', color: BLUE }}>CONTINUE WHERE YOU LEFT OFF</Typography>
                            <Typography sx={{ mt: 1, fontFamily: POPPINS, fontWeight: 600, fontSize: { xs: 20, md: 24 }, color: NAVY, lineHeight: 1.3 }}>{current.lesson.name}</Typography>
                            <Typography sx={{ mt: 0.5, fontFamily: INTER, fontSize: 13.5, color: SLATE }}>{current.course.name}</Typography>
                            <Typography sx={{ mt: 1.5, fontFamily: INTER, fontSize: 12.5, color: SLATE }}>{current.lesson.completedPrompts} of {current.lesson.totalPrompts} exercises done</Typography>
                            <Button onClick={() => onOpenLesson(current.course.id, current.lesson)} variant="contained" startIcon={<PlayArrowRoundedIcon />} sx={{ ...yellowButtonSx, mt: 2.5, px: 3 }}>Continue</Button>
                        </Box>
                        <ProgressRing percent={current.percent} size={112} thickness={5} showLabel />
                    </Box>
                )}

                <Box sx={{ ...cardSx, p: 3 }}>
                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 16, color: NAVY }}>Your progress</Typography>
                    <Stack direction="row" spacing={1.25} sx={{ mt: 2 }}>
                        <Box sx={{ flex: 1, p: 1.75, borderRadius: '14px', backgroundColor: `${BLUE}0D` }}>
                            <MenuBookRoundedIcon sx={{ color: BLUE, fontSize: 21 }} />
                            <Typography sx={{ mt: 0.5, fontFamily: POPPINS, fontWeight: 600, fontSize: 21, color: NAVY }}>{lessonsDone}</Typography>
                            <Typography sx={{ fontFamily: INTER, fontSize: 11.5, color: SLATE }}>lessons done</Typography>
                        </Box>
                        <Box sx={{ flex: 1, p: 1.75, borderRadius: '14px', backgroundColor: `${GREEN}12` }}>
                            <CheckCircleRoundedIcon sx={{ color: GREEN, fontSize: 21 }} />
                            <Typography sx={{ mt: 0.5, fontFamily: POPPINS, fontWeight: 600, fontSize: 21, color: NAVY }}>{exercisesDone}</Typography>
                            <Typography sx={{ fontFamily: INTER, fontSize: 11.5, color: SLATE }}>exercises</Typography>
                        </Box>
                    </Stack>
                    <Button onClick={() => onNavigate('profile')} endIcon={<ArrowForwardRoundedIcon />} sx={{ mt: 1.5, px: 0, fontFamily: INTER, fontWeight: 700, fontSize: 12.5, textTransform: 'none', color: BLUE }}>View profile</Button>
                </Box>
            </Box>

            {latestReviewed && <ButtonBase onClick={() => onNavigate('feedback')} sx={{ ...cardSx, width: '100%', mt: 2.5, p: 2.5, display: 'flex', alignItems: 'center', gap: 2, textAlign: 'left' }}>
                <Box sx={{ width: 46, height: 46, borderRadius: '14px', display: 'grid', placeItems: 'center', backgroundColor: `${GREEN}18` }}><RateReviewRoundedIcon sx={{ color: GREEN }} /></Box>
                <Box sx={{ flex: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11, letterSpacing: '.08em', color: SLATE }}>TEACHER FEEDBACK</Typography>
                        <Chip label="Reviewed" size="small" sx={{ height: 24, bgcolor: `${GREEN}18`, color: SUCCESS_TEXT, fontFamily: INTER, fontWeight: 700, fontSize: 11 }} />
                    </Stack>
                    <Typography sx={{ mt: 0.45, fontFamily: POPPINS, fontWeight: 600, fontSize: 15, color: NAVY }}>{latestReviewed.sentenceHanzi}</Typography>
                    <Typography sx={{ fontFamily: INTER, fontSize: 12.5, color: SLATE }}>Reviewed by your teacher · {latestReviewed.review?.teacherScore}</Typography>
                </Box>
                <ArrowForwardRoundedIcon sx={{ color: SLATE }} />
            </ButtonBase>}

            <Stack direction="row" alignItems="center" sx={{ mt: 3.5, mb: 1.5 }}>
                <Typography sx={{ flex: 1, fontFamily: POPPINS, fontWeight: 600, fontSize: 18, color: NAVY }}>Your courses</Typography>
                <Button onClick={() => onNavigate('courses')} sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 12.5, textTransform: 'none', color: BLUE }}>View all</Button>
            </Stack>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }, gap: 2 }}>
                {mockCourses.map(course => <CourseCard key={course.id} course={course} onOpen={() => onOpenCourse(course.id)} />)}
            </Box>
        </Box>
    );
}
